import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { Ruolo } from '@/types/entities/user.ts';
import type { FeniceDisponibileItem } from '../lib/api/utenti.ts';

type ModalInstance = { show(): void; hide(): void; dispose(): void };

export class GestioneUtenti extends AppComponent {
  static override componentName = 'wc-gestione-utenti';

  protected override get templateId() { return 'gestione-utenti'; }

  private currentUserId = 0;
  private modals: ModalInstance[] = [];

  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private selectedFenice: FeniceDisponibileItem | null = null;

  protected override afterRender(): void {
    this.currentUserId = this.attributeNumber('data-current-user-id');
    this.appendModals();
    this.setupActions();
    this.caricaLista();
  }

  private appendModals(): void {
    for (const el of this.$$('[data-modal]')) document.body.appendChild(el);
  }

  private getModal(selector: string): ModalInstance | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }

  private setupActions(): void {
    this.delegate.on('click', '[data-action="nuovo-utente"]', () => this.apriModaleNuovo());
    this.delegate.on('click', '[data-action="cambia-ruolo"]', (e: Event) => this.apriModaleRuolo(e));
    this.delegate.on('click', '[data-action="reset-password"]', (e: Event) => this.apriModalePassword(e));
    this.delegate.on('click', '[data-action="rimuovi-territorio"]', (e: Event) => this.handleRimuoviDalTerritorio(e));

    this.listen(document, 'input', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.matches('#modalNuovoUtente [data-field="fenice-search"]')) {
        this.handleFeniceSearch((target as HTMLInputElement).value);
      }
    });

    this.listen(document, 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      const item = target.closest('[data-fenice-pick]') as HTMLElement | null;
      if (item) {
        e.preventDefault();
        this.handleFenicePick(item);
      }
      if (target.closest('[data-action="reset-fenice"]')) this.resetSelezioneFenice();
    });

    this.listen(document, 'submit', (e: Event) => {
      const form = e.target as HTMLFormElement;
      if (form.matches('[data-form="nuovo"]'))   { e.preventDefault(); this.handleSubmitNuovo(); }
      if (form.matches('[data-form="ruolo"]'))   { e.preventDefault(); this.handleSubmitRuolo(); }
      if (form.matches('[data-form="password"]')) { e.preventDefault(); this.handleSubmitPassword(); }
    });
  }

  // --- Caricamento lista ---

  private async caricaLista(): Promise<void> {
    const container = this.$('[data-results]');
    if (!container) return;

    templateParseToElement('gestione-utenti-tabella', container, { loading: true });

    const result = await this.api.utenti.lista();
    if (!result.ok) {
      templateParseToElement('gestione-utenti-tabella', container, { error: result.error });
      return;
    }

    const { utenti, count } = result.data;
    templateParseToElement('gestione-utenti-tabella', container, {
      utenti, count, currentUserId: this.currentUserId,
    });
  }

  // --- Modale Nuovo Utente (typeahead Fenice) ---

  private apriModaleNuovo(): void {
    this.resetSelezioneFenice();
    const root = document.querySelector('#modalNuovoUtente');
    if (!root) return;
    this.nascondiValidationError(root);
    const search = root.querySelector('[data-field="fenice-search"]') as HTMLInputElement | null;
    if (search) search.value = '';
    const results = root.querySelector('[data-fenice-results]') as HTMLElement | null;
    if (results) results.innerHTML = '';
    const pwd = root.querySelector('[data-field="password"]') as HTMLInputElement | null;
    const conf = root.querySelector('[data-field="conferma"]') as HTMLInputElement | null;
    if (pwd) pwd.value = '';
    if (conf) conf.value = '';
    this.getModal('#modalNuovoUtente')?.show();
    void this.handleFeniceSearch('');
  }

  private handleFeniceSearch(q: string): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => void this.eseguiFeniceSearch(q.trim()), 200);
  }

  private async eseguiFeniceSearch(q: string): Promise<void> {
    const root = document.querySelector('#modalNuovoUtente');
    if (!root) return;
    const resultsEl = root.querySelector('[data-fenice-results]') as HTMLElement | null;
    if (!resultsEl) return;

    const result = await this.api.utenti.feniceDisponibili(q);
    if (!result.ok) {
      resultsEl.innerHTML = `<div class="text-danger small p-2">${result.error}</div>`;
      return;
    }
    templateParseToElement('gestione-utenti-fenice-righe', resultsEl, { fenice: result.data.fenice });
  }

  private handleFenicePick(item: HTMLElement): void {
    const id = Number(item.dataset.feniceId);
    const nome = item.dataset.feniceNome ?? '';
    const cognome = item.dataset.feniceCognome ?? '';
    const email = item.dataset.feniceEmail ?? '';
    const territorio = item.dataset.feniceTerritorio ?? '';
    const esisteInMaster = item.dataset.feniceEsiste === '1';

    this.selectedFenice = {
      id, nome, cognome, email, territorio,
      idTerritorio: 0,
      esisteInMaster,
    };

    const root = document.querySelector('#modalNuovoUtente');
    if (!root) return;
    root.querySelector('[data-fenice-step="search"]')?.classList.add('d-none');
    root.querySelector('[data-fenice-step="selected"]')?.classList.remove('d-none');

    const labelEl = root.querySelector('[data-fenice-selected-label]');
    if (labelEl) labelEl.textContent = `${cognome} ${nome}`;
    const emailEl = root.querySelector('[data-fenice-selected-email]');
    if (emailEl) emailEl.textContent = email;
    const terrEl = root.querySelector('[data-fenice-selected-territorio]');
    if (terrEl) terrEl.textContent = territorio;

    const pwdGroup = root.querySelector('[data-password-group]');
    const passwordRichiesta = !esisteInMaster;
    pwdGroup?.classList.toggle('d-none', !passwordRichiesta);

    const notaEsiste = root.querySelector('[data-nota-esiste]');
    notaEsiste?.classList.toggle('d-none', !esisteInMaster);
  }

  private resetSelezioneFenice(): void {
    this.selectedFenice = null;
    const root = document.querySelector('#modalNuovoUtente');
    if (!root) return;
    root.querySelector('[data-fenice-step="search"]')?.classList.remove('d-none');
    root.querySelector('[data-fenice-step="selected"]')?.classList.add('d-none');
    root.querySelector('[data-password-group]')?.classList.add('d-none');
    root.querySelector('[data-nota-esiste]')?.classList.add('d-none');
    this.nascondiValidationError(root);
  }

  private async handleSubmitNuovo(): Promise<void> {
    const root = document.querySelector('#modalNuovoUtente');
    if (!root) return;
    if (!this.selectedFenice) {
      this.mostraValidationError(root, 'Seleziona un utente Fenice');
      return;
    }

    const passwordRichiesta = !this.selectedFenice.esisteInMaster;
    let password: string | undefined;
    if (passwordRichiesta) {
      const pwd = (root.querySelector('[data-field="password"]') as HTMLInputElement).value;
      const conf = (root.querySelector('[data-field="conferma"]') as HTMLInputElement).value;
      if (pwd.length < 6) { this.mostraValidationError(root, 'La password deve avere almeno 6 caratteri'); return; }
      if (pwd !== conf)   { this.mostraValidationError(root, 'Le password non coincidono'); return; }
      password = pwd;
    }

    const ruolo = (root.querySelector('[data-field="ruolo"]:checked') as HTMLInputElement)?.value as Ruolo || 'operatore';

    const result = await this.api.utenti.crea({ idFenice: this.selectedFenice.id, ruolo, password });
    if (!result.ok) {
      this.mostraValidationError(root, result.error);
      return;
    }

    this.getModal('#modalNuovoUtente')?.hide();
    Weext.toast('Utente aggiunto al territorio', { type: 'success' });
    this.caricaLista();
  }

  // --- Modale Cambio Ruolo ---

  private apriModaleRuolo(e: Event): void {
    const btn = (e.target as HTMLElement).closest('[data-action="cambia-ruolo"]') as HTMLElement;
    const id = btn?.dataset.id;
    const ruoloAttuale = btn?.dataset.ruoloAttuale;
    const nome = btn?.dataset.nome;
    if (!id) return;

    const root = document.querySelector('#modalCambiaRuolo');
    if (!root) return;

    const nomeEl = root.querySelector('[data-ruolo-nome]');
    if (nomeEl) nomeEl.textContent = nome ?? '';

    const idEl = root.querySelector('[data-ruolo-id]') as HTMLInputElement | null;
    if (idEl) idEl.value = id;

    const radios = root.querySelectorAll('[data-field="nuovo-ruolo"]') as NodeListOf<HTMLInputElement>;
    for (const r of radios) r.checked = r.value === ruoloAttuale;

    this.getModal('#modalCambiaRuolo')?.show();
  }

  private async handleSubmitRuolo(): Promise<void> {
    const root = document.querySelector('#modalCambiaRuolo');
    if (!root) return;

    const id = Number((root.querySelector('[data-ruolo-id]') as HTMLInputElement).value);
    const nuovoRuolo = (root.querySelector('[data-field="nuovo-ruolo"]:checked') as HTMLInputElement)?.value as Ruolo;
    if (!id || !nuovoRuolo) return;

    const result = await this.api.utenti.cambiaRuolo(id, nuovoRuolo);
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }

    this.getModal('#modalCambiaRuolo')?.hide();
    Weext.toast('Ruolo aggiornato', { type: 'success' });
    this.caricaLista();
  }

  // --- Modale Reset Password ---

  private apriModalePassword(e: Event): void {
    const btn = (e.target as HTMLElement).closest('[data-action="reset-password"]') as HTMLElement;
    const id = btn?.dataset.id;
    const nome = btn?.dataset.nome;
    if (!id) return;

    const root = document.querySelector('#modalResetPassword');
    if (!root) return;

    const nomeEl = root.querySelector('[data-password-nome]');
    if (nomeEl) nomeEl.textContent = nome ?? '';

    const idEl = root.querySelector('[data-password-id]') as HTMLInputElement | null;
    if (idEl) idEl.value = id;

    for (const input of root.querySelectorAll('input:not([type="hidden"])') as NodeListOf<HTMLInputElement>) {
      input.value = '';
    }
    this.nascondiValidationError(root);
    this.getModal('#modalResetPassword')?.show();
  }

  private async handleSubmitPassword(): Promise<void> {
    const root = document.querySelector('#modalResetPassword');
    if (!root) return;

    const id = Number((root.querySelector('[data-password-id]') as HTMLInputElement).value);
    const password = (root.querySelector('[data-field="nuova-password"]') as HTMLInputElement).value;
    const conferma = (root.querySelector('[data-field="conferma-password"]') as HTMLInputElement).value;

    if (password.length < 6) { this.mostraValidationError(root, 'La password deve avere almeno 6 caratteri'); return; }
    if (password !== conferma) { this.mostraValidationError(root, 'Le password non coincidono'); return; }

    const result = await this.api.utenti.resetPassword(id, password);
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }

    this.getModal('#modalResetPassword')?.hide();
    Weext.toast('Password reimpostata', { type: 'success' });
    this.caricaLista();
  }

  // --- Rimuovi dal territorio ---

  private async handleRimuoviDalTerritorio(e: Event): Promise<void> {
    const btn = (e.target as HTMLElement).closest('[data-action="rimuovi-territorio"]') as HTMLElement;
    const id = Number(btn?.dataset.id);
    const nome = btn?.dataset.nome ?? '';
    if (!id) return;

    const ok = await Weext.confirm(
      `Rimuovere ${nome} dal territorio corrente? Manterra' eventuali accessi su altri territori.`,
      { title: 'Conferma rimozione', confirmText: 'Rimuovi', confirmClass: 'btn-danger' },
    );
    if (!ok) return;

    const result = await this.api.utenti.rimuoviDalTerritorio(id);
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }

    const msg = result.data.disattivato
      ? `${nome} rimosso e disattivato (nessun altro territorio)`
      : `${nome} rimosso dal territorio`;
    Weext.toast(msg, { type: 'success' });
    this.caricaLista();
  }

  // --- Helpers validation ---

  private mostraValidationError(root: Element, messaggio: string): void {
    const el = root.querySelector('[data-validation-error]');
    if (!el) return;
    el.textContent = messaggio;
    el.classList.remove('d-none');
  }

  private nascondiValidationError(root: Element): void {
    const el = root.querySelector('[data-validation-error]');
    if (!el) return;
    el.textContent = '';
    el.classList.add('d-none');
  }

  protected override onDisconnected(): void {
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    this.modals = [];
    for (const el of document.querySelectorAll('[data-modal="nuovo"], [data-modal="ruolo"], [data-modal="password"]')) {
      el.remove();
    }
  }
}
